/**
 * Что происходит с голосовым ответом прямо сейчас — обратно в блок задания.
 *
 * 🔴 ЗАЧЕМ ОТДЕЛЬНЫЙ КАНАЛ. Запись уходит через `lib/outbox.ts`, и сама
 * загрузка идёт ВНУТРИ обработчика очереди (`main.tsx`), вне дерева React:
 * блок про неё ничего не знает и показывал одну крутилку в кнопке. Тот же
 * приём, что у замера сервера, — `lib/recordingResult.ts`.
 *
 * 🔴 РАДИ ЧЕГО. Ученик жал «Отправить» и не знал, идёт ли что-нибудь вообще.
 * На плохой связи запись в несколько мегабайт уходит долго, и молчание в этот
 * момент читается как «платформа зависла» — ровно та беда, от которой в
 * плеере ушли 10.09.2026.
 *
 * ⚠️ СНИМОК ОБЯЗАН БЫТЬ УСТОЙЧИВЫМ ПО ССЫЛКЕ: блок читает его через
 * `useSyncExternalStore`, а тот сравнивает ссылки. Отдавая каждый раз новый
 * объект, мы получили бы бесконечную перерисовку страницы урока.
 *
 * ⚠️ И РОВНО ПОЭТОМУ ЖЕ повтор того же значения не будит подписчиков: событий
 * прогресса у загрузки десятки в секунду, и почти все — про тот же процент.
 */

export type SendPhase =
  /** Файл декодируется на клиенте: цел ли звук (lib/audioDuration.ts). */
  | 'checking'
  /** Байты уходят на сервер. */
  | 'uploading'
  /** Файл на месте, отмечаем ответ ученика. */
  | 'marking';

export type SendProgress = {
  phase: SendPhase;
  /** 0…100 или `null`, когда размер неизвестен: «Отправляю…» без числа. */
  percent: number | null;
};

const byBlock = new Map<string, SendProgress>();
const listeners = new Set<() => void>();

export function publishSendProgress(blockId: string, progress: SendProgress | null): void {
  const was = byBlock.get(blockId);
  if (progress === null) {
    if (!byBlock.delete(blockId)) return;
  } else {
    if (was && was.phase === progress.phase && was.percent === progress.percent) return;
    byBlock.set(blockId, progress);
  }
  listeners.forEach((l) => l());
}

export function sendProgressOf(blockId: string): SendProgress | undefined {
  return byBlock.get(blockId);
}

export function subscribeSendProgress(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Только для проверок. */
export function resetSendProgress(): void {
  byBlock.clear();
  listeners.clear();
}
